import React, { useEffect, useRef } from 'react';
import { X, Newspaper, MessageCircle, Mic2, ShoppingBag, Headphones, GraduationCap, Disc3 } from 'lucide-react';
import { Button } from './ui/button';
import NewsTab from './DJBooth/NewsTab';
import ForumTab from './DJBooth/ForumTab';
import AcademyTab from './DJBooth/AcademyTab';
import GigBoardTab from './DJBooth/GigBoardTab';
import MarketplaceTab from './DJBooth/MarketplaceTab';
import DJToolsTab from './DJBooth/DJToolsTab';

// Feature configuration
const FEATURES = {
  news: {
    id: 'news',
    title: 'Industry News',
    icon: Newspaper,
    component: NewsTab
  },
  forum: {
    id: 'forum',
    title: 'Community Forum',
    icon: MessageCircle,
    component: ForumTab
  },
  gigs: {
    id: 'gigs',
    title: 'Gig & Event Hub',
    icon: Mic2,
    component: GigBoardTab
  },
  marketplace: {
    id: 'marketplace',
    title: 'Marketplace & Directory',
    icon: ShoppingBag,
    component: MarketplaceTab
  },
  tools: {
    id: 'tools',
    title: 'Pro Tips & DJ Tools',
    icon: Headphones,
    component: DJToolsTab
  },
  academy: {
    id: 'academy',
    title: 'The Academy',
    icon: GraduationCap,
    component: AcademyTab
  }
};

const DJBoothFeatureModal = ({ isOpen, onClose, featureId, onShareToDiscord, onViewInMyEvents }) => {
  const modalContentRef = useRef(null);
  
  // Get feature configuration
  const feature = FEATURES[featureId];
  
  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isOpen, featureId]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !feature) return null;

  const FeatureIcon = feature.icon;
  const FeatureComponent = feature.component;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      {/* Modal Container with Gradient Border */}
      <div 
        className="dj-booth-feature-modal mini-modal-context"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          padding: '3px', // Space for gradient border
          background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff, #ff00ff)',
          backgroundSize: '300% 300%',
          animation: 'miniModalBorderFlow 4s ease-in-out infinite'
        }}
      >
        {/* Inner content container */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, rgba(10, 15, 30, 0.99), rgba(20, 25, 45, 0.99))',
          borderRadius: '17px',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 2rem',
            borderBottom: '2px solid rgba(0, 255, 255, 0.2)',
            background: 'rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <FeatureIcon size={28} style={{ color: '#00ffff' }} />
              {feature.title}
            </h2>
            
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                background: 'transparent',
                border: '2px solid rgba(0, 255, 255, 0.5)',
                color: '#00ffff',
                padding: '0.5rem',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                width: '40px',
                height: '40px',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = '#00ffff';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.5)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X size={20} />
            </Button>
          </div>

          {/* Content Area */}
          <div 
            ref={modalContentRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '2rem'
            }}
          >
            <FeatureComponent onShareToDiscord={onShareToDiscord} onViewInMyEvents={onViewInMyEvents} onDiscordShare={onShareToDiscord} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes miniModalBorderFlow {
          0%, 100% { 
            background-position: 0% 50%; 
          }
          50% { 
            background-position: 100% 50%; 
          }
        }

        /* Custom scrollbar for modal content */
        .dj-booth-feature-modal > div > div:last-child::-webkit-scrollbar {
          width: 8px;
        }

        .dj-booth-feature-modal > div > div:last-child::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
        }

        .dj-booth-feature-modal > div > div:last-child::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #00ffff, #ff00ff);
          border-radius: 10px;
        }

        .dj-booth-feature-modal > div > div:last-child::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #00ffff, #00ffff);
        }

        /* Smaller cards for Gig Hub and Marketplace in mini modal */
        .mini-modal-context [style*="grid-template-columns"] {
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important;
        }
        
        /* Reduce card padding in mini modal */
        .mini-modal-context [style*="padding: 1.5rem"] {
          padding: 1rem !important;
        }
        
        /* Smaller text in mini modal cards */
        .mini-modal-context h3 {
          font-size: 1rem !important;
        }
        
        .mini-modal-context p {
          font-size: 0.8rem !important;
        }
      `}</style>
    </div>
  );
};

export default DJBoothFeatureModal;
